/*
=========================================================
* RingFoods Pricing Section
=========================================================
*/

// @mui material components
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";

// @mui icons
import CheckIcon from "@mui/icons-material/Check";
import StarIcon from "@mui/icons-material/Star";
import PhoneIcon from "@mui/icons-material/Phone";
import PersonIcon from "@mui/icons-material/Person";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";

// Material Kit 2 PRO React components
import MKBox from "components/MKBox";
import MKTypography from "components/MKTypography";
import MKButton from "components/MKButton";
import MKBadge from "components/MKBadge";
import { brandSoftGradient, grey100 } from "pages/RingFoodsHomepage/theme";
import useSignupUrl from "hooks/useSignupUrl";
import { getDisplayTrialDays, isPromoActive, PROMO_TRIAL } from "constants/promo";

function Pricing() {
  const signupUrl = useSignupUrl();
  const pricingPlans = [
    {
      name: "Basic",
      price: "$100",
      period: "/month",
      subtitle: "Stop missing calls",
      description: "Perfect for small restaurants",
      features: [
        "200 minutes/month included",
        "$0.35 per additional minute",
        "24/7 AI Voice Agent",
        "10 premium AI voices",
        "Custom AI prompts",
        "Google Calendar integration",
        "Smart table management",
        "Holiday & special hours",
        "Free restaurant webpage + QR code",
        "Online reservations & feedback from your page",
        "Waitlist & queue management",
        "Spam call blocking",
        "Mid-call SMS links (menu, ordering, booking)",
        "Call quality scoring",
        "Call transfer to human",
        "Call transcripts & recordings",
        "Customer feedback reports",
        "Real-time analytics dashboard",
        "SMS & email notifications",
        "Customer SMS confirmations",
        "Menu OCR extraction",
        "Regulatory compliance built-in",
        "Email support",
        "1 phone number",
        "SMS at $0.15 each",
        "No setup fees",
        "Cancel anytime",
      ],
      recommended: false,
      color: "info",
      popular: false,
    },
    {
      name: "Professional",
      price: "$200",
      period: "/month",
      subtitle: "End phone stress entirely",
      description: "Most popular for growing restaurants",
      features: [
        "500 minutes/month included",
        "$0.30 per additional minute",
        "24/7 AI Voice Agent",
        "10 premium AI voices",
        "Custom AI prompts",
        "Google Calendar integration",
        "Smart table management",
        "Holiday & special hours",
        "Free restaurant webpage + QR code",
        "Online reservations, orders & feedback from your page",
        "Waitlist & queue management",
        "Spam call blocking",
        "Mid-call SMS links (menu, ordering, booking)",
        "Call quality scoring",
        "POS integration (Square, Clover, Toast, Lightspeed)",
        "Order taking & processing",
        "Automated cancellation notification calls",
        "AI waitlist offer calls",
        "Follow-up call campaigns",
        "Call transfer to human",
        "Call transcripts & recordings",
        "Customer feedback reports",
        "Real-time analytics dashboard",
        "SMS & email notifications",
        "Customer SMS confirmations",
        "Menu OCR extraction",
        "Regulatory compliance built-in",
        "Email support",
        "1 phone number",
        "SMS at $0.13 each",
        "No setup fees",
        "Cancel anytime",
      ],
      recommended: true,
      color: "success",
      popular: true,
    },
    {
      name: "Enterprise",
      price: "$300",
      period: "/month",
      subtitle: "Total phone freedom",
      description: "For high-volume restaurants",
      features: [
        "1000 minutes/month included",
        "$0.25 per additional minute",
        "24/7 AI Voice Agent",
        "10 premium AI voices",
        "Custom AI prompts",
        "Google Calendar integration",
        "Smart table management",
        "Holiday & special hours",
        "Free restaurant webpage + QR code",
        "Online reservations, orders & feedback from your page",
        "Waitlist & queue management",
        "Spam call blocking",
        "Mid-call SMS links (menu, ordering, booking)",
        "Call quality scoring",
        "POS integration (Square, Clover, Toast, Lightspeed)",
        "Order taking & processing",
        "Automated cancellation notification calls",
        "AI waitlist offer calls",
        "Follow-up call campaigns",
        "Call transfer to human",
        "Call transcripts & recordings",
        "Customer feedback reports",
        "Real-time analytics dashboard",
        "SMS & email notifications",
        "Customer SMS confirmations",
        "Menu OCR extraction",
        "Regulatory compliance built-in",
        "Email support",
        "1 phone number",
        "SMS at $0.10 each",
        "No setup fees",
        "Cancel anytime",
      ],
      recommended: false,
      color: "primary",
      popular: false,
    },
  ];

  return (
    <MKBox component="section" py={{ xs: 8, md: 12 }} id="pricing">
      <Container>
        {/* Section Header */}
        <Grid container spacing={3} sx={{ mb: { xs: 6, md: 8 } }}>
          <Grid item xs={12} lg={8} mx="auto" textAlign="center">
            <MKTypography
              variant="h2"
              fontWeight="bold"
              color="primary"
              sx={{ mb: 2, fontSize: { xs: "1.85rem", sm: "2.25rem", md: "2.5rem" } }}
            >
              Simple, Transparent Pricing
            </MKTypography>
            <MKTypography variant="body1" color="text" sx={{ fontSize: { xs: "1rem", md: "1.125rem" }, mb: 2 }}>
              No hidden fees, no long-term contracts. Pay only for what you use.
            </MKTypography>
            <MKTypography variant="body2" color="text" sx={{ opacity: 0.7 }}>
              {isPromoActive() ? `${PROMO_TRIAL.name} — start` : "Start"} with a{" "}
              {getDisplayTrialDays()}-day free trial. Cancel anytime with no penalties.
            </MKTypography>
          </Grid>
        </Grid>

        {/* Pricing Plans */}
        <Grid container spacing={3} sx={{ mb: 8 }}>
          {pricingPlans.map((plan, index) => (
            <Grid item xs={12} md={4} key={index}>
              <Card
                sx={{
                  height: "100%",
                  position: "relative",
                  border: plan.popular ? "2px solid" : "1px solid",
                  borderColor: plan.popular ? "success.main" : "grey.200",
                  "&:hover": {
                    transform: "translateY(-8px)",
                    transition: "all 0.3s ease",
                    boxShadow: "0 20px 40px rgba(0,0,0,0.1)",
                  },
                }}
              >
                {plan.popular && (
                  <MKBox
                    sx={{
                      position: "absolute",
                      top: -10,
                      left: "50%",
                      transform: "translateX(-50%)",
                    }}
                  >
                    <MKBadge
                      badgeContent="MOST POPULAR"
                      variant="contained"
                      color="success"
                      size="sm"
                    />
                  </MKBox>
                )}

                <CardContent sx={{ p: { xs: 3, md: 4 }, textAlign: "center" }}>
                  {/* Title */}
                  <MKTypography variant="h4" fontWeight="bold" sx={{ mb: 1 }}>
                    {plan.name}
                  </MKTypography>

                  <MKTypography
                    variant="body1"
                    fontWeight={700}
                    sx={{
                      mb: 0.5,
                      fontSize: { xs: "0.95rem", md: "1.05rem" },
                      color: plan.popular ? "success.main" : "primary.main",
                    }}
                  >
                    {plan.subtitle}
                  </MKTypography>

                  <MKTypography variant="caption" color="text" sx={{ mb: 3, opacity: 0.6, display: "block" }}>
                    {plan.description}
                  </MKTypography>

                  {/* Price */}
                  <MKBox sx={{ mb: 4 }}>
                    <MKTypography
                      variant="h2"
                      component="p"
                      fontWeight="bold"
                      color={plan.color + ".main"}
                      sx={{ display: "inline", fontSize: { xs: "2rem", md: "3rem" } }}
                    >
                      {plan.price}
                    </MKTypography>
                    <MKTypography
                      variant="h6"
                      color="text"
                      sx={{ display: "inline", opacity: 0.7, fontSize: { xs: "1rem", md: "1.25rem" } }}
                    >
                      {plan.period}
                    </MKTypography>
                  </MKBox>

                  {/* Features */}
                  <MKBox textAlign="left" sx={{ mb: 4 }}>
                    {plan.features.map((feature, featureIndex) => (
                      <MKBox
                        key={featureIndex}
                        sx={{
                          display: "flex",
                          alignItems: "flex-start",
                          mb: { xs: 1, md: 1.25 },
                        }}
                      >
                        <CheckIcon sx={{ color: "success.main", mr: 1, fontSize: { xs: 16, md: 20 }, mt: 0.25, flexShrink: 0 }} />
                        <MKTypography variant="body2" color="text" sx={{ fontSize: { xs: "0.8rem", md: "0.875rem" }, lineHeight: 1.4 }}>
                          {feature}
                        </MKTypography>
                      </MKBox>
                    ))}
                  </MKBox>

                  {/* CTA Button */}
                  <MKButton
                    variant={plan.popular ? "contained" : "outlined"}
                    size="large"
                    fullWidth
                    onClick={() => window.location.href = signupUrl}
                    sx={{
                      py: { xs: 1.25, md: 1.5 },
                      cursor: "pointer",
                      ...(plan.popular
                        ? {
                            backgroundColor: "#1A1A1A !important",
                            color: "#FFFFFF !important",
                            fontWeight: 600,
                            "&:hover": {
                              backgroundColor: "#333333 !important",
                            },
                          }
                        : {
                            borderColor: "#1A1A1A !important",
                            color: "#1A1A1A !important",
                            fontWeight: 600,
                            "&:hover": {
                              borderColor: "#1A1A1A !important",
                              backgroundColor: "rgba(26, 26, 26, 0.05)",
                            },
                          }),
                    }}
                  >
                    {plan.popular ? "Start Free Trial" : "Get Started"}
                  </MKButton>

                  {plan.popular && (
                    <MKTypography variant="caption" color="text" sx={{ mt: 1, opacity: 0.7 }}>
                      Not charged until trial ends &bull; Cancel anytime
                    </MKTypography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* ROI Breakdown */}
        <Grid container spacing={3} sx={{ mb: 8 }}>
          <Grid item xs={12}>
            <Card
              sx={{
                background: brandSoftGradient,
                border: "1px solid rgba(26, 26, 26, 0.1)",
              }}
            >
              <CardContent sx={{ p: { xs: 2.5, md: 4 }, textAlign: "center" }}>
                <MKTypography variant="h4" fontWeight="bold" sx={{ mb: { xs: 2, md: 3 }, fontSize: { xs: "1.25rem", md: "2.125rem" } }}>
                  Why RingFoods AI Pays for Itself
                </MKTypography>

                <Grid container spacing={{ xs: 2, md: 4 }}>
                  <Grid item xs={12} md={4}>
                    <MKBox sx={{ mb: { xs: 1, md: 2 } }}>
                      <TrendingUpIcon sx={{ fontSize: { xs: 28, md: 40 }, color: "success.main" }} />
                    </MKBox>
                    <MKTypography variant="h5" fontWeight="bold" sx={{ mb: 1, fontSize: { xs: "1rem", md: "1.5rem" } }}>
                      Capture Lost Revenue
                    </MKTypography>
                    <MKTypography variant="body2" color="text" sx={{ opacity: 0.8, fontSize: { xs: "0.8rem", md: "0.875rem" } }}>
                      Convert missed and after-hours calls into booked reservations and answered questions
                    </MKTypography>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <MKBox sx={{ mb: { xs: 1, md: 2 } }}>
                      <PersonIcon sx={{ fontSize: { xs: 28, md: 40 }, color: "info.main" }} />
                    </MKBox>
                    <MKTypography variant="h5" fontWeight="bold" sx={{ mb: 1, fontSize: { xs: "1rem", md: "1.5rem" } }}>
                      Protect Your Team
                    </MKTypography>
                    <MKTypography variant="body2" color="text" sx={{ opacity: 0.8, fontSize: { xs: "0.8rem", md: "0.875rem" } }}>
                      Staff focus on guests instead of phones. No more absorbing hostile callers or juggling rush-hour interruptions
                    </MKTypography>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <MKBox sx={{ mb: { xs: 1, md: 2 } }}>
                      <StarIcon sx={{ fontSize: { xs: 28, md: 40 }, color: "warning.main" }} />
                    </MKBox>
                    <MKTypography variant="h5" fontWeight="bold" sx={{ mb: 1, fontSize: { xs: "1rem", md: "1.5rem" } }}>
                      Guard Your Reputation
                    </MKTypography>
                    <MKTypography variant="body2" color="text" sx={{ opacity: 0.8, fontSize: { xs: "0.8rem", md: "0.875rem" } }}>
                      Every call answered means fewer &quot;couldn&apos;t reach anyone&quot; reviews damaging your Google rating
                    </MKTypography>
                  </Grid>
                </Grid>

                <MKBox
                  sx={{ mt: { xs: 2, md: 4 }, p: { xs: 2, md: 3 }, backgroundColor: "rgba(76, 175, 80, 0.1)", borderRadius: 2 }}
                >
                  <MKTypography variant="h6" fontWeight="bold" color="success.main" sx={{ mb: 1, fontSize: { xs: "0.9rem", md: "1.25rem" } }}>
                    Estimate your monthly impact
                  </MKTypography>
                  <MKTypography variant="body2" color="text" sx={{ opacity: 0.8, fontSize: { xs: "0.8rem", md: "0.875rem" } }}>
                    Use the ROI calculator above to model missed calls, staffing time, and expected value for your restaurant.
                  </MKTypography>
                </MKBox>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* FAQ Style Pricing Info */}
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <MKBox sx={{ p: { xs: 2, md: 3 }, borderRadius: 2, backgroundColor: grey100 }}>
              <MKTypography variant="h6" fontWeight="bold" sx={{ mb: { xs: 1, md: 2 }, fontSize: { xs: "0.95rem", md: "1.25rem" } }}>
                What&apos;s included in each plan?
              </MKTypography>
              <MKTypography variant="body2" color="text" sx={{ lineHeight: 1.6, fontSize: { xs: "0.8rem", md: "0.875rem" } }}>
                All plans include AI voice handling, Google Calendar integration, analytics,
                and a free restaurant webpage with online reservations, waitlist, feedback,
                and a printable QR code. Professional and Enterprise plans add POS integration,
                order taking, and outbound calling &mdash; automated cancellation notifications
                and AI waitlist offer calls. Additional minutes beyond your plan are billed at
                competitive per-minute rates.
              </MKTypography>
            </MKBox>
          </Grid>

          <Grid item xs={12} md={6}>
            <MKBox sx={{ p: { xs: 2, md: 3 }, borderRadius: 2, backgroundColor: grey100 }}>
              <MKTypography variant="h6" fontWeight="bold" sx={{ mb: { xs: 1, md: 2 }, fontSize: { xs: "0.95rem", md: "1.25rem" } }}>
                What about angry callers?
              </MKTypography>
              <MKTypography variant="body2" color="text" sx={{ lineHeight: 1.6, fontSize: { xs: "0.8rem", md: "0.875rem" } }}>
                All plans include our feedback specialist. AI handles complaints with empathy,
                records details, and alerts management &mdash; no human absorbs the stress. Your staff
                never has to deal with hostile callers again.
              </MKTypography>
            </MKBox>
          </Grid>

          <Grid item xs={12} md={6}>
            <MKBox sx={{ p: { xs: 2, md: 3 }, borderRadius: 2, backgroundColor: grey100 }}>
              <MKTypography variant="h6" fontWeight="bold" sx={{ mb: { xs: 1, md: 2 }, fontSize: { xs: "0.95rem", md: "1.25rem" } }}>
                What if I exceed my monthly minutes?
              </MKTypography>
              <MKTypography variant="body2" color="text" sx={{ lineHeight: 1.6, fontSize: { xs: "0.8rem", md: "0.875rem" } }}>
                Great problem to have! Additional minutes are billed at $0.35/min (Basic),
                $0.30/min (Professional), or $0.25/min (Enterprise). If you consistently
                exceed your limit, consider upgrading to a higher tier for more included
                minutes and better overage rates.
              </MKTypography>
            </MKBox>
          </Grid>

          <Grid item xs={12} md={6}>
            <MKBox sx={{ p: { xs: 2, md: 3 }, borderRadius: 2, backgroundColor: grey100 }}>
              <MKTypography variant="h6" fontWeight="bold" sx={{ mb: { xs: 1, md: 2 }, fontSize: { xs: "0.95rem", md: "1.25rem" } }}>
                Can I cancel anytime?
              </MKTypography>
              <MKTypography variant="body2" color="text" sx={{ lineHeight: 1.6, fontSize: { xs: "0.8rem", md: "0.875rem" } }}>
                Absolutely. No contracts, no cancellation fees. Cancel anytime.
              </MKTypography>
            </MKBox>
          </Grid>
        </Grid>
      </Container>
    </MKBox>
  );
}

export default Pricing;
